// Descobrir que saiu versao nova do app — sem depender de alguem recarregar.
//
// O problema apareceu na pratica: a troca de senha obrigatoria foi publicada,
// o dono entrou para conferir e entrou direto, sem a tela nova. O servidor
// estava certo, a conta estava certa; o navegador dele e que ainda rodava o
// pacote anterior. Nao havia erro em lugar nenhum — o que faz este tipo de
// falha passar por "funcionou".
//
// Num sistema onde o que se publica e correcao de dado clinico, "a pessoa
// recarrega quando lembrar" nao serve.
//
// COMO: o index.html aponta para o pacote com hash no nome. Se o index que
// esta no servidor agora nao cita o arquivo que ESTA rodando aqui, saiu versao
// nova. Nao precisa de arquivo de versao, nem de passo a mais no build — a
// pergunta e respondida pelo que ja existe.

/**
 * O arquivo deste proprio modulo, com o hash do build.
 *
 * Em desenvolvimento o Vite serve modulos soltos e isto nao vale nada: por
 * isso a checagem inteira e desligada fora de producao.
 */
const MEU_PACOTE = import.meta.url.split('/').pop() ?? '';
const EM_PRODUCAO = import.meta.env.PROD;

/** De quanto em quanto tempo perguntar, com a aba aberta. */
const INTERVALO_MS = 10 * 60_000;

async function saiuVersaoNova(): Promise<boolean> {
  if (!EM_PRODUCAO || !MEU_PACOTE.endsWith('.js')) return false;
  try {
    const r = await fetch('/index.html', { cache: 'no-store' });
    if (!r.ok) return false;
    const html = await r.text();
    // Se o index do servidor nao cita o pacote que esta rodando, ele mudou.
    return !html.includes(MEU_PACOTE);
  } catch {
    // Sem rede a pergunta nao tem resposta — e "nao sei" nunca vira "sim".
    return false;
  }
}

/**
 * Comeca a vigiar. Chama `aoDetectar` uma unica vez, quando houver versao nova.
 *
 * Pergunta ao voltar para a aba, porque e quando a pessoa vai usar o app de
 * novo — e no celular do hospital a aba passa horas em segundo plano.
 */
export function vigiarVersao(aoDetectar: () => void): () => void {
  if (!EM_PRODUCAO) return () => {};
  let avisado = false;

  const conferir = async () => {
    if (avisado || document.hidden) return;
    // O service worker tambem se atualiza: sem isto ele serviria a casca velha
    // depois do recarregamento.
    navigator.serviceWorker?.getRegistration().then((r) => r?.update()).catch(() => {});
    if (await saiuVersaoNova()) {
      avisado = true;
      aoDetectar();
    }
  };

  const t = setInterval(() => void conferir(), INTERVALO_MS);
  const aoVoltar = () => { if (!document.hidden) void conferir(); };
  document.addEventListener('visibilitychange', aoVoltar);
  // A primeira pergunta nao e na hora: recem-carregado, a versao e a do servidor.
  const primeira = setTimeout(() => void conferir(), 60_000);

  return () => {
    clearInterval(t);
    clearTimeout(primeira);
    document.removeEventListener('visibilitychange', aoVoltar);
  };
}

/**
 * Recarrega de verdade: joga fora o cache do service worker antes.
 *
 * Sem limpar, o recarregamento podia voltar a servir a mesma casca antiga, e a
 * pessoa clicaria em "atualizar" para nada — o pior resultado possivel, porque
 * ela passaria a nao acreditar no aviso.
 */
export async function atualizarAgora(): Promise<void> {
  try {
    const chaves = await caches.keys();
    await Promise.all(chaves.map((k) => caches.delete(k)));
    const registro = await navigator.serviceWorker?.getRegistration();
    await registro?.update();
  } catch {
    // Se nao deu para limpar, recarregar mesmo assim ainda e melhor do que nada.
  }
  location.reload();
}
